// @vero/pdf — read the text of a PDF in the browser, offline, with Mozilla pdf.js (vendored, see vendor/pdfjs/README.md).
// pdf.js understands real-world PDFs (embedded subset fonts with ToUnicode maps, Greek text, object streams) that the
// small built-in reader (domain/pdf-text.ts) cannot. It runs in the page: no worker file, no network, no eval.
// Scanned PDFs (images only) have no text: `chars` is then 0 and the caller offers manual entry.
import { linesFromTextItems, type PdfTextItem } from '../../domain/src/pdf-layout.ts'; // direct: keeps the website reader bundle small

export interface PdfReadResult {
  pages: string[][];
  /** number of characters found (0 = no text layer: a scan) */
  chars: number;
  error?: 'not_pdf' | 'password' | 'damaged';
}

type PdfJs = typeof import('../vendor/pdfjs/pdf.mjs');
let loading: Promise<PdfJs> | null = null;
/** pdf.js is large: loaded the first time a PDF is read, then kept */
function load(): Promise<PdfJs> {
  loading ??= (async () => {
    const worker = await import('../vendor/pdfjs/pdf.worker.mjs');
    // pdf.js runs its "worker" on the main thread when this global is set (no separate worker file to ship)
    (globalThis as { pdfjsWorker?: unknown }).pdfjsWorker = worker;
    return import('../vendor/pdfjs/pdf.mjs');
  })();
  return loading;
}

export async function readPdf(bytes: Uint8Array, opts: { maxPages?: number } = {}): Promise<PdfReadResult> {
  const head = String.fromCharCode(...bytes.subarray(0, Math.min(1024, bytes.length)));
  if (!head.includes('%PDF')) return { pages: [], chars: 0, error: 'not_pdf' };
  const pdfjs = await load();
  const task = pdfjs.getDocument({ data: bytes.slice(), isEvalSupported: false, disableFontFace: true, useSystemFonts: false, stopAtErrors: false, verbosity: 0, useWorkerFetch: false, isOffscreenCanvasSupported: false });
  let needPassword = false;
  task.onPassword = () => { needPassword = true; void task.destroy(); };
  try {
    const doc = await task.promise;
    const pages: string[][] = [];
    let chars = 0;
    const n = Math.min(doc.numPages, opts.maxPages ?? 20);
    for (let i = 1; i <= n; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const items: PdfTextItem[] = [];
      for (const it of content.items) {
        if (!('str' in it)) continue;
        const [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0] = it.transform;
        items.push({ str: it.str, x: e, y: f, w: it.width, h: it.height || Math.hypot(c, d) || Math.hypot(a, b) });
        chars += it.str.trim().length;
      }
      pages.push(linesFromTextItems(items));
      page.cleanup();
    }
    await doc.destroy();
    return { pages, chars };
  } catch {
    if (needPassword) return { pages: [], chars: 0, error: 'password' };
    return { pages: [], chars: 0, error: 'damaged' };
  }
}
